import {
  ApprovalStatus,
  BusinessSize,
  CaptainRole,
  CaptainSummary,
  CaptainTeam,
  CreatedCaptain,
  DEFAULT_CAPTAIN_ROLE,
  DEFAULT_SETTLEMENT_PROGRAM,
  PaymentLinkResult,
  PaymentStatus,
  PendingBusiness,
  PayerType,
  SettledCaptain,
  Settlement,
  PayoutDetails,
  SettlementRequest,
  SettlementRequestStatus,
  TeamCaptain,
  unsettledOf,
} from "../../../interfaces/OnboardingObject";
import { coerceSingle } from "../../helpers/ApiEnvelopeHelper";

// Onboarding APIs are already camelCase (new convention) — these converters
// only null-guard and coerce numbers so a partial backend response can never
// crash the UI or render NaN.

const toNumber = (value: unknown): number => Number(value ?? 0) || 0;

/**
 * Number when present, undefined when absent — for lead-commission fields that
 * must render as "not applicable" (non-lead) rather than a misleading ₹0.
 * null (explicit "not a lead") and undefined (omitted) both map to undefined.
 */
const toOptionalNumber = (value: unknown): number | undefined =>
  value === undefined || value === null ? undefined : Number(value) || 0;

const toApprovalStatus = (value: unknown): ApprovalStatus => {
  return value === "APPROVED" || value === "REJECTED" ? value : "PENDING";
};

/** Undefined (not a fallback) when absent — pre-payments rows have no badge. */
const toPaymentStatus = (value: unknown): PaymentStatus | undefined => {
  return value === "UNPAID" || value === "PAID" || value === "NOT_REQUIRED"
    ? value
    : undefined;
};

/** Undefined when absent — rows from before businessSize became required.
 *  No longer a strict "S"/"M"/"L" allowlist: the dropdown that produces this
 *  value now sources it from the (open-ended) subscriptions plan catalog, so
 *  any non-empty string is accepted rather than silently dropped. */
const toBusinessSize = (value: unknown): BusinessSize | undefined => {
  return typeof value === "string" && value ? value : undefined;
};

/**
 * Normalises the role string. Absent or unrecognised → "CAPTAIN".
 *
 * Defaulting rather than throwing is deliberate and is what makes this
 * forward-compatible: the field doesn't exist on the wire yet, and until it does
 * every summary belongs to a captain. Upper-cased first so a backend that emits
 * "curator" or "Both" still resolves.
 */
const toCaptainRole = (value: unknown): CaptainRole => {
  if (typeof value !== "string") return DEFAULT_CAPTAIN_ROLE;
  const normalised = value.trim().toUpperCase();
  if (normalised === "OPERATOR") return "OPERATOR";
  return normalised === "CURATOR" ||
    normalised === "BOTH" ||
    normalised === "CAPTAIN"
    ? (normalised as CaptainRole)
    : DEFAULT_CAPTAIN_ROLE;
};

export function convertCaptainSummary(
  contents: Record<string, unknown>
): CaptainSummary {
  const totalCaptainCommission = toNumber(contents?.totalCaptainCommission);
  const totalLeadCommission = toOptionalNumber(contents?.totalLeadCommission);
  const totalCuratorCommission = toOptionalNumber(contents?.totalCuratorCommission);
  const totalSettled = toNumber(contents?.totalSettled);

  // CLIENT-COMPUTED: CaptainSummaryDTO has no totalUnsettled field on the
  // wire — reading contents?.totalUnsettled always evaluated to 0.
  const totalUnsettled = unsettledOf({
    totalCaptainCommission,
    totalLeadCommission,
    totalCuratorCommission,
    totalSettled,
  });

  return {
    captainId: String(contents?.captainId ?? ""),
    name: String(contents?.name ?? ""),
    isLead: contents?.isLead === true,
    role: toCaptainRole(contents?.role),
    totalCaptainCommission,
    // Present only when isLead — kept undefined otherwise so the UI shows no
    // lead-commission tile for non-leads.
    totalLeadCommission,
    // Present only when canCurate(role) — kept undefined otherwise so the UI
    // shows no curator-commission tile for a pure captain.
    totalCuratorCommission,
    totalSettled,
    totalUnsettled,
    totalOnboardings: toNumber(contents?.totalOnboardings),
    pendingOnboardings: toNumber(contents?.pendingOnboardings),
  };
}

export function convertPendingBusiness(
  raw: Record<string, unknown>
): PendingBusiness {
  return {
    pendingId: String(raw?.pendingId ?? ""),
    businessName: String(raw?.businessName ?? ""),
    contactPhone: String(raw?.contactPhone ?? ""),
    nineSetupId: raw?.nineSetupId ? String(raw.nineSetupId) : undefined,
    onboardingFee: toNumber(raw?.onboardingFee),
    finalFee: toNumber(raw?.finalFee),
    approvalStatus: toApprovalStatus(raw?.approvalStatus),
    businessId: raw?.businessId ? String(raw.businessId) : null,
    createdAt: String(raw?.createdAt ?? ""),
    businessSize: toBusinessSize(raw?.businessSize),
    paymentRequired:
      typeof raw?.paymentRequired === "boolean" ? raw.paymentRequired : undefined,
    paymentStatus: toPaymentStatus(raw?.paymentStatus),
    paymentShortUrl: raw?.paymentShortUrl ? String(raw.paymentShortUrl) : null,
    paymentLinkExpiresAt: raw?.paymentLinkExpiresAt
      ? String(raw.paymentLinkExpiresAt)
      : null,
    invoiceUrl: raw?.invoiceUrl ? String(raw.invoiceUrl) : null,
  };
}

/** contents of retry-payment-link → PaymentLinkResult. Unlike PendingBusiness's
 *  paymentStatus (which may be absent on pre-rollout rows), this endpoint
 *  always sets it — default to UNPAID only as a last-resort guard. */
export function convertPaymentLinkResult(
  raw: Record<string, unknown>
): PaymentLinkResult {
  return {
    paymentRequired:
      typeof raw?.paymentRequired === "boolean" ? raw.paymentRequired : true,
    shortUrl: raw?.shortUrl ? String(raw.shortUrl) : null,
    expiresAt: raw?.expiresAt ? String(raw.expiresAt) : null,
    paymentStatus: toPaymentStatus(raw?.paymentStatus) ?? "UNPAID",
  };
}

/**
 * Shape-tolerant list reader shared by every list endpoint (the backend uses
 * legacy response builders — see ApiEnvelopeHelper). `contents` may be:
 *  1. { <wrapperKey>: [...] }          — doc shape
 *  2. [ { <wrapperKey>: [...] } ]      — array-wrapped wrapper
 *  3. [ {<idKey>, ...}, ... ]          — raw rows directly in contents
 * Detection assumption: every row carries one of `rowKeys` (its PK) and the
 * wrapper object never does — so probing contents[0] cleanly separates shape 3
 * from shape 2. Anything else → [].
 */
function convertList<T>(
  contents: unknown,
  rowKeys: readonly string[],
  wrapperKey: string,
  convertRow: (raw: Record<string, unknown>) => T
): T[] {
  if (Array.isArray(contents)) {
    const first = contents[0] as Record<string, unknown> | undefined;
    if (first && rowKeys.some((key) => first[key] !== undefined)) {
      return contents.map((row) => convertRow(row as Record<string, unknown>));
    }
  }
  const rows = coerceSingle(contents)[wrapperKey];
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => convertRow(row as Record<string, unknown>));
}

/** contents of get-pending-businesses-by-captain-id → PendingBusiness[]. */
export function convertPendingBusinessList(contents: unknown): PendingBusiness[] {
  return convertList(contents, ["pendingId"], "pendingBusinesses", convertPendingBusiness);
}

export function convertTeamCaptain(
  raw: Record<string, unknown>
): TeamCaptain {
  return {
    captainId: String(raw?.captainId ?? ""),
    // Auth user id — lets the roster match a member to their settlement
    // request (requests are keyed by user, not captain).
    userId: raw?.userId ? String(raw.userId) : undefined,
    name: String(raw?.name ?? ""),
    phone: raw?.phone ? String(raw.phone) : undefined,
    role: toCaptainRole(raw?.role),
    totalCaptainCommission: toNumber(raw?.totalCaptainCommission),
    isLead: raw?.isLead === true,
    // Present only if this member is themselves a lead — else "not applicable".
    totalLeadCommission: toOptionalNumber(raw?.totalLeadCommission),
    // Present only if this member is a curator (role CURATOR or BOTH) — else
    // "not applicable".
    totalCuratorCommission: toOptionalNumber(raw?.totalCuratorCommission),
    // Returned by both get-captains-by-lead-id and settle-captain.
    totalSettled: toNumber(raw?.totalSettled),
    totalOnboardings: toNumber(raw?.totalOnboardings),
    createdAt: String(raw?.createdAt ?? ""),
    commissionRate: toOptionalNumber(raw?.commissionRate),
  };
}

/** contents of get-captains-by-lead-id → CaptainTeam (roster only — team-level
 *  totals are not read; the roster screen sums its own rows). */
export function convertCaptainTeam(contents: unknown): CaptainTeam {
  return {
    captains: convertList(contents, ["captainId"], "captains", convertTeamCaptain),
  };
}

/** contents of create-captain → CreatedCaptain. */
export function convertCreatedCaptain(
  contents: Record<string, unknown>
): CreatedCaptain {
  return {
    ...convertTeamCaptain(contents),
    userId: String(contents?.userId ?? ""),
    leadId: String(contents?.leadId ?? ""),
    isLead: contents?.isLead === true,
  };
}

/** contents of settle-captain → SettledCaptain. Same as create-captain plus a
 *  definite totalSettled (the field the caller reads to refresh the row). */
export function convertSettledCaptain(
  contents: Record<string, unknown>
): SettledCaptain {
  return {
    ...convertCreatedCaptain(contents),
    totalSettled: toNumber(contents?.totalSettled),
  };
}
// ── Settlements ──

const toNullableText = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const text = String(value);
  return text === "" ? null : text;
};

const toPayerType = (value: unknown): PayerType =>
  value === "USER" || value === "ADMIN" ? value : "SYSTEM";

const toRequestStatus = (value: unknown): SettlementRequestStatus =>
  value === "SETTLED" || value === "CANCELLED" ? value : "PENDING";

export function convertSettlement(raw: Record<string, unknown>): Settlement {
  return {
    settlementId: String(raw?.settlementId ?? raw?.id ?? ""),
    program: String(raw?.program ?? DEFAULT_SETTLEMENT_PROGRAM),
    payeeUserId: String(raw?.payeeUserId ?? ""),
    amount: toNumber(raw?.amount),
    settledAt: String(raw?.settledAt ?? ""),
    payerType: toPayerType(raw?.payerType),
    payerName: toNullableText(raw?.payerName),
    note: toNullableText(raw?.note),
    reference: toNullableText(raw?.reference),
  };
}

/** contents of get-settlements → Settlement[]. */
export function convertSettlementList(contents: unknown): Settlement[] {
  return convertList(contents, ["settlementId", "id"], "settlements", convertSettlement);
}

export function convertSettlementRequest(
  raw: Record<string, unknown>
): SettlementRequest {
  return {
    requestId: String(raw?.requestId ?? raw?.id ?? ""),
    program: String(raw?.program ?? DEFAULT_SETTLEMENT_PROGRAM),
    payeeUserId: String(raw?.payeeUserId ?? ""),
    payeeName: String(raw?.payeeName ?? ""),
    amount: toNumber(raw?.amount),
    status: toRequestStatus(raw?.status),
    payerType: raw?.payerType === "ADMIN" ? "ADMIN" : "USER",
    payerName: toNullableText(raw?.payerName),
    note: toNullableText(raw?.note),
    requestedAt: String(raw?.requestedAt ?? ""),
    resolvedAt: toNullableText(raw?.resolvedAt),
    settlementId: toNullableText(raw?.settlementId),
    payout: convertPayoutDetails(raw?.payout),
  };
}

/** Request payout snapshot; null when absent or not a known method. */
export function convertPayoutDetails(raw: unknown): PayoutDetails | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  if (rec.method !== "UPI" && rec.method !== "BANK") return null;
  return {
    method: rec.method,
    upiId: toNullableText(rec.upiId),
    holder: toNullableText(rec.holder),
    account: toNullableText(rec.account),
    ifsc: toNullableText(rec.ifsc),
    phone: toNullableText(rec.phone),
  };
}

/** contents of get-settlement-requests → SettlementRequest[]. */
export function convertSettlementRequestList(contents: unknown): SettlementRequest[] {
  return convertList(
    contents,
    ["requestId", "id"],
    "requests",
    convertSettlementRequest
  );
}
